const AUTOPLAY_DELAY = 7000;
const SWIPE_THRESHOLD = 50;

class HeroCarousel {
    constructor(root) {
        this.root = root;
        this.slidesEl = root.querySelector('[data-hero-carousel-slides]');
        this.slides = Array.from(root.querySelectorAll('[data-hero-carousel-slide]'));
        this.dots = Array.from(root.querySelectorAll('[data-hero-carousel-dot]'));
        this.toggle = root.querySelector('[data-hero-carousel-toggle]');
        this.fadeDuration = HeroCarousel.toMilliseconds(getComputedStyle(root).getPropertyValue('--hero-carousel-fade'));
        this.index = 0;
        this.timer = null;
        this.leaveTimer = null;
        this.holds = new Set();
        this.isPlaying = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    static toMilliseconds(value) {
        const amount = parseFloat(value) || 0;
        return value.trim().endsWith('ms') ? amount : amount * 1000;
    }

    init() {
        if (this.slides.length < 2 || !this.toggle) {
            return;
        }

        this.toggle.addEventListener('click', () => this.setPlaying(!this.isPlaying));

        this.dots.forEach((dot, index) => {
            dot.addEventListener('click', () => this.goTo(index, true));
        });

        this.root.addEventListener('pointerenter', (e) => {
            if (e.pointerType === 'mouse') {
                this.hold('hover');
            }
        });

        this.root.addEventListener('pointerleave', (e) => {
            if (e.pointerType === 'mouse') {
                this.release('hover');
            }
        });

        this.root.addEventListener('focusin', (e) => {
            if (this.isKeyboardFocus(e.target)) {
                this.setPlaying(false);
            }
        });

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.hold('hidden');
            } else {
                this.release('hidden');
            }
        });

        this.bindSwipe();
        this.watchWizard();
        this.setPlaying(this.isPlaying);
    }

    goTo(next, fromUser = false) {
        if (next === this.index) {
            return;
        }

        const leaving = this.slides[this.index];
        const entering = this.slides[next];

        this.updateLiveRegion(fromUser);

        window.clearTimeout(this.leaveTimer);
        this.slides.forEach((slide) => slide.classList.remove('is-leaving'));

        leaving.classList.replace('is-active', 'is-leaving');
        leaving.inert = true;
        entering.classList.add('is-active');
        entering.inert = false;

        this.leaveTimer = window.setTimeout(() => leaving.classList.remove('is-leaving'), this.fadeDuration);

        this.dots.forEach((dot, index) => {
            dot.classList.toggle('is-active', index === next);

            if (index === next) {
                dot.setAttribute('aria-current', 'true');
            } else {
                dot.removeAttribute('aria-current');
            }
        });

        this.index = next;
        this.schedule();
    }

    step(direction, fromUser = false) {
        const count = this.slides.length;
        this.goTo((this.index + direction + count) % count, fromUser);
    }

    setPlaying(isPlaying) {
        this.isPlaying = isPlaying;
        this.toggle.setAttribute('aria-label', isPlaying ? 'Pause slides' : 'Play slides');
        this.root.classList.toggle('is-paused', !isPlaying);
        this.updateLiveRegion();
        this.schedule();
    }

    // Announce slide changes someone makes; stay quiet while auto-rotating
    updateLiveRegion(fromUser = false) {
        this.slidesEl.setAttribute('aria-live', fromUser || !this.isPlaying ? 'polite' : 'off');
    }

    hold(reason) {
        this.holds.add(reason);
        this.schedule();
    }

    release(reason) {
        this.holds.delete(reason);
        this.schedule();
    }

    schedule() {
        window.clearTimeout(this.timer);

        if (this.isPlaying && this.holds.size === 0) {
            this.timer = window.setTimeout(() => this.step(1), AUTOPLAY_DELAY);
        }
    }

    isKeyboardFocus(target) {
        try {
            return target.matches(':focus-visible');
        } catch (e) {
            return false;
        }
    }

    bindSwipe() {
        let startX = null;
        let startY = 0;

        this.slidesEl.addEventListener('touchstart', (e) => {
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
        }, { passive: true });

        this.slidesEl.addEventListener('touchend', (e) => {
            if (startX === null) {
                return;
            }

            const deltaX = e.changedTouches[0].clientX - startX;
            const deltaY = e.changedTouches[0].clientY - startY;
            startX = null;

            if (Math.abs(deltaX) >= SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY)) {
                this.step(deltaX < 0 ? 1 : -1, true);
            }
        }, { passive: true });
    }

    // The wizard's steps panel covers the hero, so rotation holds while it's open
    watchWizard() {
        const wizard = document.getElementById('wizard-app');

        if (!wizard || !('MutationObserver' in window)) {
            return;
        }

        new MutationObserver(() => {
            if (wizard.querySelector('.wizard-steps')) {
                this.hold('wizard');
            } else {
                this.release('wizard');
            }
        }).observe(wizard, { childList: true, subtree: true });
    }
}

export default HeroCarousel;

(() => {
    document.querySelectorAll('[data-hero-carousel]').forEach((root) => new HeroCarousel(root).init());
})();
